import { useEffect, useState } from 'react';

/**
 * Renders an hours provider's badge art.
 *
 * The provider itself declares what to draw (server-side, in getProviderInfo),
 * so this component never maps provider names to assets:
 *
 *   { kind: 'image', src: '/res/providers/clockify.png', alt: 'Clockify' }
 *   { kind: 'icon',  className: 'bi bi-pencil-square',   alt: 'Manual Entry' }
 *
 * A missing or broken image falls back to a neutral glyph rather than showing a
 * broken-image box, so a provider works before anyone uploads its logo.
 */
export default function ProviderIcon({ icon, size = 18, fallbackClassName = 'bi bi-database', style }) {
  const [imageFailed, setImageFailed] = useState(false);
  const src = icon?.kind === 'image' ? icon.src : null;

  // Retry the image when the provider (and therefore the src) changes.
  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const boxStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    ...style,
  };

  if (src && !imageFailed) {
    return (
      <span style={boxStyle}>
        <img
          src={src}
          alt={icon?.alt || ''}
          onError={() => setImageFailed(true)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      </span>
    );
  }

  const className = icon?.kind === 'icon' && icon?.className ? icon.className : fallbackClassName;

  return (
    <span style={boxStyle}>
      <i
        className={className}
        aria-hidden="true"
        style={{ fontSize: Math.round(size * 0.85), lineHeight: 1, color: '#666' }}
      />
    </span>
  );
}
